import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  myTeamMonthlyCutoffsService,
  type MyTeamCompany,
  type MyTeamMonthlyCutoff,
} from '@/api/domains/attendance/myTeam';
import { useAbortController } from '@/hooks/ui/useAbortController';
import { useUI } from '@/hooks/ui/useUI';


type SelectOption = { value: string; label: string };

// Filters panel state for the "Mi equipo" dashboard "Resumen del mes" section
// (ASIS-5414). Receives the companies catalog already loaded by `useMyTeamFilters`
// (real supervised companies only) so it does not duplicate that fetch — this hook
// only owns the Empresa/Año/Mes/Descripción selection and the monthly cutoffs it
// derives from the selected company.
//
// Uses the SAME `companies` array as the GENERAL "Situación de asistencia" Empresa
// filter — only really-supervised companies are selectable here too (business rule
// confirmed 2026-08-11: no special-casing for the holding's parent company).
//
// `selectedCompanyId`/`selectedYear`/`selectedMonth`/`selectedMonthlyCutId` are
// exposed already-resolved (not just raw setters) so future tickets in the epic
// (ASIS-5409 a 5413 — the indicator cards/charts) can consume the resolved
// company/monthlyCutId without duplicating this preselection logic.
export interface UseMyTeamMonthlySummaryFiltersResult {
  selectedCompanyId: string;
  selectedYear: string;
  selectedMonth: string;
  selectedMonthlyCutId: string;
  companyOptions: SelectOption[];
  yearOptions: SelectOption[];
  monthOptions: SelectOption[];
  descriptionOptions: SelectOption[];
  isLoadingCutoffs: boolean;
  // False only once the cutoffs for the selected company loaded SUCCESSFULLY and
  // came back empty — a fetch error must NOT trigger this message (that's a
  // different, already-toasted problem, not "esta empresa no tiene cortes").
  hasAvailableCutoffs: boolean;
  isFiltersDisabled: boolean;
  onCompanyChange: (companyId: string) => void;
  onYearChange: (year: string) => void;
  onMonthChange: (month: string) => void;
  onDescriptionChange: (monthlyCutId: string) => void;
}

// The backend already returns cutoffs ordered desc by recency (year desc, month
// desc), so the most recent cut is simply the first element — no re-sort needed.
function findMostRecentCutoff(cutoffs: MyTeamMonthlyCutoff[]): MyTeamMonthlyCutoff | null {
  return cutoffs[0] ?? null;
}

export function useMyTeamMonthlySummaryFilters(
  companies: MyTeamCompany[],
  // Empresa única resuelta desde el filtro general "Situación de asistencia" (arriba
  // en el dashboard) cuando ese multi-select tiene EXACTAMENTE una empresa marcada;
  // null cuando tiene 0 ("Todas") o 2+ — en esos casos este panel usa su propia
  // preselección (empresa padre / primera de la lista) sin depender del filtro
  // general. Mantiene ambos selectores de Empresa sincronizados en esa dirección.
  externalSelectedCompanyId?: string | null,
  // Notifica al filtro general cuando el usuario cambia la Empresa DESDE este panel,
  // para que ese multi-select pase a tener únicamente esa empresa seleccionada
  // (sincronización en la dirección opuesta).
  onExternalCompanyChange?: (companyId: string) => void,
): UseMyTeamMonthlySummaryFiltersResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();
  const { createSignal } = useAbortController();

  const [selectedCompanyId, setSelectedCompanyIdState] = useState('');
  const [cutoffs, setCutoffs] = useState<MyTeamMonthlyCutoff[]>([]);
  const [selectedYear, setSelectedYearState] = useState('');
  const [selectedMonth, setSelectedMonthState] = useState('');
  const [selectedMonthlyCutId, setSelectedMonthlyCutIdState] = useState('');
  const [isLoadingCutoffs, setIsLoadingCutoffs] = useState(false);
  const [hasLoadedCutoffsOnce, setHasLoadedCutoffsOnce] = useState(false);
  const [hasFetchError, setHasFetchError] = useState(false);

  // Aplica un cambio de empresa limpiando año/mes/descripción sincrónicamente, para
  // que no quede seleccionado un período que no existe para la nueva empresa
  // mientras el fetch de sus cortes sigue en vuelo (el efecto de fetchCutoffs abajo
  // dispara la recarga). Compartido por la preselección inicial, la sincronización
  // desde el filtro general y el cambio manual del usuario en este panel.
  const applyCompanyChange = useCallback((companyId: string) => {
    setSelectedCompanyIdState(companyId);
    setSelectedYearState('');
    setSelectedMonthState('');
    setSelectedMonthlyCutIdState('');
  }, []);

  // Sincronización desde el filtro general (arriba): si ese multi-select resuelve a
  // una única empresa, "Resumen del mes" la adopta, reemplazando cualquier selección
  // propia previa. Tiene prioridad sobre la preselección de empresa padre de abajo.
  useEffect(() => {
    if (externalSelectedCompanyId == null) return;
    if (externalSelectedCompanyId === selectedCompanyId) return;
    applyCompanyChange(externalSelectedCompanyId);
  }, [externalSelectedCompanyId, selectedCompanyId, applyCompanyChange]);

  // Preselección de Empresa (AC): la primera empresa realmente supervisada de la
  // lista, como fallback razonable en vez de dejar el panel sin selección. Solo
  // corre mientras no haya selección propia NI una empresa impuesta por el filtro
  // general (esa la resuelve el efecto de arriba).
  useEffect(() => {
    if (externalSelectedCompanyId != null) return;
    if (selectedCompanyId || companies.length === 0) return;
    setSelectedCompanyIdState(String(companies[0].id));
  }, [companies, selectedCompanyId, externalSelectedCompanyId]);

  const fetchCutoffs = useCallback(async (companyId: string) => {
    const signal = createSignal();
    setIsLoadingCutoffs(true);

    const response = await myTeamMonthlyCutoffsService.getMonthlyCutoffs(
      { cutoffCompanyId: Number(companyId) },
      signal,
    );
    if (response.error?.code === 'ABORTED') return;
    setIsLoadingCutoffs(false);
    setHasLoadedCutoffsOnce(true);

    if (!response.success || !response.data) {
      showApiError(response, translate('attendance:dashboard.myTeam.monthSummary.filters.loadError'));
      setHasFetchError(true);
      setCutoffs([]);
      setSelectedYearState('');
      setSelectedMonthState('');
      setSelectedMonthlyCutIdState('');
      return;
    }

    setHasFetchError(false);
    const items = response.data;
    setCutoffs(items);

    const mostRecent = findMostRecentCutoff(items);
    if (mostRecent) {
      setSelectedYearState(String(mostRecent.year));
      setSelectedMonthState(String(mostRecent.month));
      setSelectedMonthlyCutIdState(String(mostRecent.id));
    } else {
      setSelectedYearState('');
      setSelectedMonthState('');
      setSelectedMonthlyCutIdState('');
    }
  }, [createSignal, showApiError, translate]);

  useEffect(() => {
    if (!selectedCompanyId) return;
    void fetchCutoffs(selectedCompanyId);
  }, [selectedCompanyId, fetchCutoffs]);

  const companyOptions = useMemo<SelectOption[]>(
    () => companies.map(
      (company) => ({ value: String(company.id), label: company.name }),
    ),
    [companies],
  );

  const yearOptions = useMemo<SelectOption[]>(() => {
    const uniqueYears = [...new Set(cutoffs.map((cutoff) => cutoff.year))].sort((a, b) => b - a);
    return uniqueYears.map((year) => ({ value: String(year), label: String(year) }));
  }, [cutoffs]);

  const monthOptions = useMemo<SelectOption[]>(() => {
    const monthsForYear = cutoffs
      .filter((cutoff) => String(cutoff.year) === selectedYear)
      .map((cutoff) => cutoff.month);
    const uniqueMonths = [...new Set(monthsForYear)].sort((a, b) => b - a);
    return uniqueMonths.map((month) => ({
      value: String(month),
      label: translate(`attendance:dashboard.months.${month - 1}`),
    }));
  }, [cutoffs, selectedYear, translate]);

  const descriptionOptions = useMemo<SelectOption[]>(() => {
    const monthIndexLabel = translate(`attendance:dashboard.months.${Number(selectedMonth) - 1}`);
    return cutoffs
      .filter(
        (cutoff) => String(cutoff.year) === selectedYear && String(cutoff.month) === selectedMonth,
      )
      .map((cutoff) => ({
        value: String(cutoff.id),
        label: cutoff.description ?? monthIndexLabel,
      }));
  }, [cutoffs, selectedYear, selectedMonth, translate]);

  // Cambio manual de Empresa DESDE este panel: aplica el cambio localmente y avisa
  // al filtro general para que quede con esa única empresa seleccionada (sincronía
  // en la dirección opuesta al efecto de arriba, que reacciona a cambios del filtro
  // general). No dispara el efecto de sincronización de arriba porque, al notificar
  // primero, externalSelectedCompanyId terminará resolviendo al mismo valor ya
  // aplicado aquí.
  const onCompanyChange = useCallback((companyId: string) => {
    applyCompanyChange(companyId);
    onExternalCompanyChange?.(companyId);
  }, [applyCompanyChange, onExternalCompanyChange]);

  const onYearChange = useCallback((year: string) => {
    setSelectedYearState(year);
    const monthsForNewYear = cutoffs
      .filter((cutoff) => String(cutoff.year) === year)
      .map((cutoff) => cutoff.month)
      .sort((a, b) => b - a);
    const newMonth = monthsForNewYear[0];
    if (newMonth === undefined) {
      setSelectedMonthState('');
      setSelectedMonthlyCutIdState('');
      return;
    }
    setSelectedMonthState(String(newMonth));
    const firstCutForPeriod = cutoffs.find(
      (cutoff) => String(cutoff.year) === year && cutoff.month === newMonth,
    );
    setSelectedMonthlyCutIdState(firstCutForPeriod ? String(firstCutForPeriod.id) : '');
  }, [cutoffs]);

  const onMonthChange = useCallback((month: string) => {
    setSelectedMonthState(month);
    const firstCutForPeriod = cutoffs.find(
      (cutoff) => String(cutoff.year) === selectedYear && String(cutoff.month) === month,
    );
    setSelectedMonthlyCutIdState(firstCutForPeriod ? String(firstCutForPeriod.id) : '');
  }, [cutoffs, selectedYear]);

  const onDescriptionChange = useCallback((monthlyCutId: string) => {
    setSelectedMonthlyCutIdState(monthlyCutId);
  }, []);

  // Antes de la primera carga se asume disponibilidad (evita parpadear el mensaje de
  // advertencia mientras el fetch inicial está en vuelo); solo se vuelve `false` una
  // vez confirmado (carga EXITOSA) que la empresa seleccionada no tiene cortes. Un
  // error de fetch NO debe mostrar este mensaje — `showApiError` ya informó el
  // problema real, y "sin cortes disponibles" sería una explicación incorrecta.
  const hasAvailableCutoffs = hasFetchError || !hasLoadedCutoffsOnce || cutoffs.length > 0;
  // La deshabilitación, en cambio, depende únicamente de si hay opciones reales para
  // elegir — sigue aplicando en error (no hay cortes que mostrar, sea cual sea la
  // causa), a diferencia del mensaje de arriba.
  const isFiltersDisabled = hasLoadedCutoffsOnce && cutoffs.length === 0;

  return {
    selectedCompanyId,
    selectedYear,
    selectedMonth,
    selectedMonthlyCutId,
    companyOptions,
    yearOptions,
    monthOptions,
    descriptionOptions,
    isLoadingCutoffs,
    hasAvailableCutoffs,
    isFiltersDisabled,
    onCompanyChange,
    onYearChange,
    onMonthChange,
    onDescriptionChange,
  };
}

export default useMyTeamMonthlySummaryFilters;
